import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { CommitmentSummaryListItem } from '../../coaching-base/general';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { dedup } from '../../coaching-tags/tags/tags.function';
import {
    CriteriaIdType,
    MetricData,
    PerformanceDataViewType,
} from '../../insight/performance-data/metric.pd.model';
import { PerformanceDataService } from '../../insight/performance-data/performance-data.service';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { PeriodType } from '../../widget/date-range.widget.model';
import { Column, Row } from '../experimental.grid';

import {
    ActionType,
    KpiSetReloadAction,
    kpiSetReloadResult,
} from './kpi-set.observation.action';
import { makeColumns, makeRows } from './kpi-set.observation.function';
import {
    CellContext,
    ColumnContext,
    RowContext,
} from './kpi-set.observation.model';
import { KpiSetState } from './kpi-set.observation.state';

interface ChainGroup<TPayload> {
    root: TPayload;

    metricDataSet?: MetricData[];
    commitmentSummary?: CommitmentSummaryListItem[];

    gridColumns?: Array<Column<ColumnContext>>;
    gridRows?: Array<Row<RowContext, CellContext>>;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const kpiSetReload = (
    action: ActionsObservable<PayloadAction<ActionType, KpiSetReloadAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_KPI_SET__RELOAD),
        concatMap((act) => {
            const rootState = pluck<KpiSetState>(state.value, act.storePath);
            const pdService = deps.injector.get<PerformanceDataService>(
                PerformanceDataService,
            );
            const cService =
                deps.injector.get<CommitmentService>(CommitmentService);
            const grp: ChainGroup<KpiSetReloadAction> = {
                root: act.payload,
                gridColumns: rootState.gridColumns,
                gridRows: rootState.gridRows,
            };

            const metricIds = dedup<number>(
                act.payload.metrics.map((e) => e.metricId),
                (e) => e,
            );
            let obs: Observable<MetricData[]> = of([]);
            if (metricIds.length > 0) {
                obs = pdService
                    .listDataToMetricData({
                        viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                        displayNameCriteriaIdType: CriteriaIdType.USER_ID,
                        outputCriteriaIdType: CriteriaIdType.USER_ID,
                        periodType: PeriodType.ROLLING_30,
                        date: new Date(),
                        userIdList: [act.payload.performerId],
                        metricIdList: metricIds,
                    })
                    .pipe(catchError(() => of<MetricData[]>([])));
            }

            return obs.pipe(
                // eslint-disable-next-line no-return-assign
                tap((ret) => (grp.metricDataSet = ret)),
                // TODO revise. expensive and doesn't really work here. will probably be replaced by a more appropriate api call
                cService.listSummary(() => ({
                    userId: act.payload.performerId,
                })),
                // eslint-disable-next-line no-return-assign
                tap((ret) => (grp.commitmentSummary = ret)),
                tap(() => {
                    // if any of the widths are invalid values, reset widths
                    if (
                        !grp.gridColumns ||
                        grp.gridColumns.some(
                            (c) =>
                                typeof c.width !== 'number' ||
                                !isFinite(c.width),
                        )
                    ) {
                        grp.gridColumns = makeColumns();
                    }

                    grp.gridRows = makeRows(
                        act.payload.behaviourId,
                        act.payload.subBehaviourId,
                        metricIds,
                        act.payload.metricsLookup,
                        grp.metricDataSet,
                        grp.commitmentSummary,
                        act.payload.metricId,
                    );
                }),
                map(() =>
                    kpiSetReloadResult(
                        {
                            ...act.payload,
                            gridColumns: grp.gridColumns,
                            gridRows: grp.gridRows,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        kpiSetReloadResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

export default [kpiSetReload];
